import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const LOGIN_BG = 'https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NTgxMTN8MA&ixlib=rb-4.1.0&q=85';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate('/', { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8 lg:p-16">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tighter mb-2">
            Training Journey
          </h1>
          <p className="font-body text-base text-[#4B5563] mb-10">
            Sign in to your account
          </p>

          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 text-sm font-body">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">
                Username
              </Label>
              <Input
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 rounded-none border-[#E5E7EB] focus:ring-2 focus:ring-[#0047FF] focus:border-transparent font-body"
                placeholder="Enter your username"
                required
              />
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">
                Password
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-[#E5E7EB] focus:ring-2 focus:ring-[#0047FF] focus:border-transparent font-body"
                placeholder="Enter your password"
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body font-semibold h-11 transition-colors duration-150"
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>

      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url('${LOGIN_BG}')` }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </div>
  );
}
